import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { useAuthStore, useFeedsStore } from '../store/useStore';
import { feeds as feedsApi } from '../lib/api';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import type { Components } from 'react-markdown';

interface Digest {
  id: string;
  type: 'personal' | 'instance';
  startDate: string;
  endDate: string;
  summary: string;
  content: string;
  metadata: string;
  createdAt: string;
}

// The backend appends the Reading Recap under this heading.
const RECAP_HEADING = /^##\s+.*Reading Recap\s*$/m;
const QUIET_WEEK_PREFIX = 'A quiet week';

function splitDigest(content: string): { week: string; recap: string } {
  const match = content.match(RECAP_HEADING);
  if (!match || match.index === undefined) {
    return { week: content.trim(), recap: '' };
  }
  const week = content.slice(0, match.index).trim();
  // The recap keeps its own H2; markdown headings are the section labels.
  const recap = content.slice(match.index).trim();
  return { week, recap };
}

function hasReadingData(metadata: string, type: Digest['type']): boolean {
  try {
    const m = JSON.parse(metadata);
    if (type === 'personal') {
      return (m.reads || 0) + (m.likes || 0) + (m.shares || 0) + (m.comments || 0) + (m.starred || 0) > 0;
    }
    return (m.totalReads || 0) + (m.totalLikes || 0) + (m.totalShares || 0) + (m.totalComments || 0) > 0;
  } catch {
    return false;
  }
}

export default function DigestReader() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token)!;
  const setFeeds = useFeedsStore((state) => state.setFeeds);
  const [digest, setDigest] = useState<Digest | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscribingFeed, setSubscribingFeed] = useState<string | null>(null);

  // Click handlers for feed suggestions and article titles rendered from markdown
  useEffect(() => {
    if (!digest) return;

    const feedCards = document.querySelectorAll('.digest-feed-suggestion');
    const handleFeedClick = (e: Event) => {
      const card = e.currentTarget as HTMLElement;
      const feedUrl = card.dataset.feedUrl;
      const feedTitle = card.querySelector('.digest-feed-title')?.textContent;

      if (feedUrl && feedTitle && !subscribingFeed) {
        handleFeedSubscribe(feedUrl, feedTitle);
      }
    };

    feedCards.forEach(card => card.addEventListener('click', handleFeedClick));

    const articleTitles = document.querySelectorAll('.digest-article-title');
    const handleArticleClick = (e: Event) => {
      const titleElement = e.currentTarget as HTMLElement;
      const card = titleElement.closest('.digest-article-card') as HTMLElement;
      const articleId = card?.dataset.articleId;
      if (articleId) {
        navigate(`/article/${articleId}`);
      }
    };

    articleTitles.forEach(title => title.addEventListener('click', handleArticleClick));

    return () => {
      feedCards.forEach(card => card.removeEventListener('click', handleFeedClick));
      articleTitles.forEach(title => title.removeEventListener('click', handleArticleClick));
    };
  }, [digest, subscribingFeed, navigate]);

  const subsectionTitles = useMemo(() => new Set<string>([
    'Topic Connections',
    'Discovery & Exploration',
    'Social Engagement',
  ]), []);

  async function handleFeedSubscribe(feedUrl: string, feedTitle: string) {
    if (subscribingFeed) return;

    setSubscribingFeed(feedUrl);
    try {
      const response = await fetch('/api/feeds/subscribe', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: feedUrl }),
      });

      if (!response.ok) {
        const error = await response.json();
        if (error.error?.includes('Already subscribed')) {
          toast.error(`Already subscribed to ${feedTitle}`);
        } else {
          throw new Error(error.error || 'Failed to subscribe');
        }
        return;
      }

      toast.success(`Subscribed to ${feedTitle}`);

      try {
        const updatedFeeds = await feedsApi.getAll(token);
        setFeeds(updatedFeeds);
      } catch (error) {
        console.error('Error refreshing feeds list:', error);
      }
    } catch (error) {
      console.error('Error subscribing to feed:', error);
      toast.error('Failed to subscribe to feed');
    } finally {
      setSubscribingFeed(null);
    }
  }

  const markdownComponents: Components = useMemo(() => ({
    h3: ({ children, node: _node, ...props }) => {
      const text = String(children);
      if (subsectionTitles.has(text)) {
        return <h3 {...props} className="digest-subsection">{children}</h3>;
      }
      return <h3 {...props}>{children}</h3>;
    },
    p: ({ children, node: _node, ...props }) => {
      const text = String(children);
      if (text.trim().startsWith('&lt;!--') || text.trim().startsWith('<!--')) {
        return null;
      }
      return <p {...props}>{children}</p>;
    },
  }), [subsectionTitles]);

  useEffect(() => {
    if (id) {
      fetchDigest(id);
    }
  }, [id]);

  async function fetchDigest(digestId: string) {
    try {
      const response = await fetch(`/api/digests/${digestId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch digest');

      const data = await response.json();
      setDigest(data);
    } catch (error) {
      console.error('Error fetching digest:', error);
      toast.error('Failed to load digest');
      navigate('/digest');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    if (!digest) return;

    if (!confirm('Delete this digest? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/digests/${digest.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to delete digest');

      toast.success('Digest deleted');
      navigate('/digest');
    } catch (error) {
      console.error('Error deleting digest:', error);
      toast.error('Failed to delete digest');
    }
  }

  if (loading) {
    return (
      <div className="pl-4 lg:pl-12 py-10">
        <span className="kicker">Loading…</span>
      </div>
    );
  }

  if (!digest) {
    return null;
  }

  const { week, recap } = splitDigest(digest.content);
  const showWeek = hasReadingData(digest.metadata, digest.type) && week.length > 0 && !week.startsWith(QUIET_WEEK_PREFIX);
  const range = `${format(new Date(digest.startDate), 'MMM d')} – ${format(new Date(digest.endDate), 'MMM d, yyyy')}`;

  return (
    <div className="bg-canvas min-h-full">
      <div className="sticky top-0 z-10 flex items-stretch border-b border-edge bg-canvas">
        <button onClick={() => navigate('/digest')} className="tool">
          <ArrowLeft className="w-3.5 h-3.5" /> Digests
        </button>
        <span className="flex-1" />
        <button onClick={handleDelete} className="tool border-l border-edge border-r-0 hover:text-signal" title="Delete digest">
          <Trash2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Delete</span>
        </button>
      </div>

      <div className="pl-4 pr-4 lg:pl-12 lg:pr-8 py-8">
        <div className="measure-prose">
          {/* Date range as a stamp, no headline hero */}
          <div className="kicker mb-1">
            {digest.type === 'personal' ? 'Personal digest' : 'Instance digest'} · generated {format(new Date(digest.createdAt), 'MMM d')}
          </div>
          <div className="font-mono text-xl text-ink tracking-tight mb-8">{range}</div>

          {showWeek && (
            <section className="mb-10">
              <article className="digest-article">
                <ReactMarkdown components={markdownComponents} rehypePlugins={[rehypeRaw]}>
                  {week}
                </ReactMarkdown>
              </article>
            </section>
          )}

          {recap && (
            <section>
              <article className="digest-article">
                <ReactMarkdown components={markdownComponents} rehypePlugins={[rehypeRaw]}>
                  {recap}
                </ReactMarkdown>
              </article>
            </section>
          )}

          {!showWeek && !recap && (
            <p className="text-ink-secondary">{digest.summary}</p>
          )}
        </div>
      </div>
    </div>
  );
}
